import {
  AlertCircle,
  CheckCircle,
  Edit2,
  Eye,
  Trash2,
  Unlink,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRole } from "@/hooks/auth";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { DocumentTypeIcon } from "./DocumentTypeIcon";
import { FolderBadge } from "./FolderBadge";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieDocumentCardProps {
  document: DocumentWithMetadata;
  onClick?: () => void;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDetach?: () => void;
  showActions?: boolean;
  brand?: string;
  /** The user_id assigned to the genie this card is rendered under, used to flag ownership mismatches. */
  genieUserId?: string | null;
}

export const GenieDocumentCard = ({
  document: doc,
  onClick,
  onView,
  onEdit,
  onDelete,
  onDetach,
  showActions = false,
  brand = "#4F9CFF",
  genieUserId,
}: GenieDocumentCardProps) => {
  const { isInternalAdmin } = useRole();

  const ownerLabel = doc.owner?.full_name || doc.owner?.email;
  const isOwnershipMismatch =
    isInternalAdmin() && !!genieUserId && !!doc.user_id && doc.user_id !== genieUserId;

  return (
    <div
      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2.5 rounded-lg border transition-all duration-200 w-full max-w-full overflow-hidden ${
        doc.el_doc_id
          ? "bg-gradient-to-br from-genie-primary/5 to-genie-secondary/5 border-genie-primary/20 sm:hover:shadow-sm sm:hover:border-genie-primary/40"
          : "bg-white border-gray-200 sm:hover:shadow-sm sm:hover:border-blue-300"
      } ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center w-full min-w-0">
        <div className="relative mr-2.5 shrink-0">
          <GenieIcon name="files" size={28} label="Knowledge document" className="h-7 w-7" />
          {doc.el_doc_id ? (
            <CheckCircle className="h-3.5 w-3.5 text-genie-primary bg-white rounded-full absolute -top-1 -right-1" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 text-genie-secondary bg-white rounded-full absolute -top-1 -right-1" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 min-w-0 max-w-full">
            <h3 className="font-medium text-sm text-genie-text truncate pr-1">{doc.name}</h3>
            <span className="text-xs text-gray-400">
              {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "N/A"}
            </span>
            <DocumentTypeIcon document={doc} iconSize="md" />
            {doc.el_doc_id ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-primary/10 text-genie-primary">
                <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                In Knowledge Base
              </span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-secondary/10 text-genie-secondary">
                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                Not in Knowledge Base
              </span>
            )}
            {doc.is_public ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-primary/10 text-genie-primary">
                Public
              </span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                Private
              </span>
            )}
            <FolderBadge folderId={doc.folder_id} size="sm" />
            {ownerLabel && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                <UserRound className="h-2.5 w-2.5" />
                <CopyableEmail email={ownerLabel} />
              </span>
            )}
            {isOwnershipMismatch && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">
                      <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                      Owner mismatch
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This document's owner doesn't match the genie's assigned user</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {doc.metadata?.description && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{doc.metadata.description}</p>
          )}
        </div>
      </div>
      {showActions && (
        <div
          className="hidden sm:flex items-center gap-1.5 mt-2 sm:mt-0 sm:whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
        >
          {doc.source_path && onView && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-genie-primary hover:text-genie-primary-hover hover:bg-genie-primary-light"
                    onClick={onView}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View source file</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-genie-primary hover:bg-genie-primary-light"
                    onClick={onEdit}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onDetach && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                    onClick={onDetach}
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Remove from this genie</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onDelete && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
};
